import type { ReactNode } from 'react';
import { Code } from '../CodeSample';
import { GuideHeader } from './GuideHeader';
import { codes, problemExample } from './errorData';

/** Problem details, the codes, and which calls to retry. */
export function ErrorsGuide(): ReactNode {
  return (
    <article className="docs-page">
      <GuideHeader slug="errors" />
      <Shape />
      <Codes />
      <Retries />
    </article>
  );
}

function Shape(): ReactNode {
  return (
    <section className="docs-section" aria-labelledby="shape-title">
      <h2 id="shape-title">Every error has the same shape</h2>
      <p>
        An error is <code>application/problem+json</code> (RFC 9457). Branch on <code>code</code>:
        it is stable, and new codes may be added, so treat one you do not know by its HTTP status.{' '}
        <code>detail</code> is the sentence to show a person. <code>requestId</code>, also the{' '}
        <code>X-Request-Id</code> header on every answer, is what to quote to support; send your own{' '}
        <code>X-Request-Id</code> (letters, digits, <code>.</code> <code>_</code> <code>:</code>{' '}
        <code>-</code>, up to 64) and Paige uses it. A <code>POST</code> with no body still sends{' '}
        <code>Content-Length: 0</code> (curl: <code>-d ''</code>); without it the request is refused
        411 before it reaches Paige.
      </p>
      <Code code={problemExample} label="an error" />
    </section>
  );
}

function Codes(): ReactNode {
  return (
    <section className="docs-section" aria-labelledby="codes-title">
      <h2 id="codes-title">The codes an API key meets</h2>
      <table className="docs-table">
        <thead>
          <tr>
            <th scope="col">Status</th>
            <th scope="col">Code</th>
            <th scope="col">What to do</th>
          </tr>
        </thead>
        <tbody>
          {codes.map((c) => (
            <tr key={c.code}>
              <td>{c.status}</td>
              <th scope="row">
                <code>{c.code}</code>
              </th>
              <td>{c.meaning}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

function Retries(): ReactNode {
  return (
    <section className="docs-section" aria-labelledby="retries-title">
      <h2 id="retries-title">What to retry</h2>
      <ul className="docs-list">
        <li>
          <strong>A lost answer, a timeout, a 5xx:</strong> send the same request again with the{' '}
          <strong>same</strong> <code>Idempotency-Key</code>, waiting longer each time (1 s, 2 s, 4
          s, and so on). If the first one went through you get its answer, marked{' '}
          <code>Idempotency-Replayed: true</code>, and nothing happens twice.
        </li>
        <li>
          <strong>
            429 and 409 <code>idempotency_in_progress</code>:
          </strong>{' '}
          wait the seconds <code>Retry-After</code> says, then send it again.
        </li>
        <li>
          <strong>Any other 4xx:</strong> the request is wrong as it stands; sending it again gives
          the same answer. Fix what <code>detail</code> says.
        </li>
      </ul>
    </section>
  );
}
